import { useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import {
  addOptimisticUserMessage,
  aronErrorText,
  markOptimisticMessageFailed,
  useCreateAronConversation,
  useSendAronMessage,
  useStopAronTurn,
} from "../api/aronApi";
import { useAronLive } from "./useAronStream";
import { useAronUiStore } from "../store/aronUiStore";
import { buildPageContext } from "../utils/pageContext";

/**
 * Shared ARON turn orchestration — live subscription + send/stop dispatch. Lives
 * in a hook so both the full page (URL-driven `selectedId`) and the detached dock
 * (store-driven `selectedId`) drive the same flow. `select` is invoked when a new
 * conversation is auto-created on the first message of a fresh chat.
 *
 * Must be hosted in a STABLE (non-remounting) parent: `sending` drives the Stop
 * button immediately on dispatch (independent of when the assistant row lands in
 * cache) and is cleared by the stream's terminal event for the active turn.
 */
export function useAronChat(
  selectedId: string | null,
  select: (id: string | null) => void,
  /** Kontekst persony dokładany przy zakładaniu wątku (np. DLC_12: sprawa PDF). */
  personaContext?: Record<string, string>,
) {
  const qc = useQueryClient();

  // Which conversation currently has a turn in flight (NOT a global flag — the
  // "ARON myśli…" state must not leak into a different/new conversation).
  const [sendingConvId, setSendingConvId] = useState<string | null>(null);
  const activeAssistantRef = useRef<string | null>(null);

  const { ensureSubscribed } = useAronLive(selectedId, (messageId) => {
    // Any terminal event (message.end / error / cancelled) on the subscribed
    // channel means the in-flight turn finished — clear the dispatch flag.
    setSendingConvId(null);
    if (messageId === activeAssistantRef.current) activeAssistantRef.current = null;
  });
  const send = useSendAronMessage();
  const stop = useStopAronTurn();
  const createConv = useCreateAronConversation();
  const modelMode = useAronUiStore((s) => s.modelMode);
  const accessLevel = useAronUiStore((s) => s.accessLevel);
  const autopilotDefault = useAronUiStore((s) => s.autopilotDefault);
  const unlimitedStepsDefault = useAronUiStore((s) => s.unlimitedStepsDefault);
  const location = useLocation();

  const handleSend = async (
    text: string,
    files?: File[],
    /** Opcje TEJ wysyłki — na razie jedna: kompresja obrazów wybrana w kompozytorze. */
    opts?: { compressImages?: boolean },
  ) => {
    let id = selectedId;
    let tempUserId: string | null = null;
    setSendingConvId(id);
    // Where the user IS right now (dock next to a detail page) — lets ARON resolve
    // "ta pozycja"/"ten kosztorys" to the on-screen object. Null on the ARON page
    // itself (no useful signal there).
    const pageContext = buildPageContext(location.pathname, location.search);
    try {
      if (!id) {
        const conv = await createConv.mutateAsync({
          mode: modelMode,
          accessLevel,
          autopilot: autopilotDefault,
          unlimitedSteps: unlimitedStepsDefault,
          context: personaContext,
        });
        id = conv.id;
        select(id);
      }
      setSendingConvId(id);
      tempUserId = addOptimisticUserMessage(qc, id, text, files);
      await ensureSubscribed(id); // subscribe BEFORE dispatch — no missed deltas
      const res = await send.mutateAsync({
        conversationId: id,
        content: text,
        tempUserId,
        files,
        compressImages: opts?.compressImages,
        pageContext: pageContext ?? undefined,
      });
      activeAssistantRef.current = res.assistant_message_id;
    } catch (err) {
      // Odrzucona wysyłka (zły typ załącznika, wyczerpany limit tokenów, tura w
      // toku, padnięta sieć) musi być WIDOCZNA. Wcześniej catch tylko gasił
      // „ARON myśli…", więc wiadomość zostawała w wątku bez odpowiedzi i bez
      // śladu przyczyny — wyglądało to na zawieszonego asystenta.
      setSendingConvId(null);
      if (id && tempUserId) markOptimisticMessageFailed(qc, id, tempUserId, aronErrorText(err));
    }
  };

  const handleStop = () => {
    if (selectedId) stop.mutate(selectedId);
    setSendingConvId(null);
  };

  const sending = sendingConvId !== null && sendingConvId === selectedId;
  return { sending, handleSend, handleStop };
}
